import axios from 'axios';
import { API_URL } from '../utils/apiConfig';
import { invalidateContactQueries } from '../utils/queryClient';

const api = axios.create({
  baseURL: API_URL
});

// Helper function to get token
const getToken = () => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.token;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
  }
  return null;
};

// Add auth token to all requests
api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const contactService = {
  // Get all contacts for the authenticated user
  async getContacts(page = 1, limit = 50, search = '') {
    try {
      const params = new URLSearchParams();
      
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      if (search) params.append('search', search);
      
      const queryString = params.toString();
      const endpoint = queryString ? `/contacts?${queryString}` : '/contacts';
      
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw error;
    }
  },

  // Get a single contact by ID
  async getContact(contactId) {
    try {
      const response = await api.get(`/contacts/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching contact:', error);
      throw error;
    }
  },

  // Create a new contact
  async createContact(contactData) {
    try {
      const response = await api.post('/contacts', contactData);
      
      // ✅ CACHE INVALIDATION: Invalidate contacts after creation
      invalidateContactQueries();
      console.log('🔄 Cache invalidated after contact creation');
      
      return response.data;
    } catch (error) {
      console.error('❌ Error creating contact:', error);
      throw error;
    }
  },

  // Update an existing contact
  async updateContact(contactId, contactData) {
    try {
      const response = await api.put(`/contacts/${contactId}`, contactData);
      
      // ✅ CACHE INVALIDATION: Invalidate contacts after update
      invalidateContactQueries();
      console.log('🔄 Cache invalidated after contact update');
      
      return response.data;
    } catch (error) {
      console.error('❌ Error updating contact:', error);
      throw error;
    }
  },

  // Delete a contact
  async deleteContact(contactId) {
    try {
      const response = await api.delete(`/contacts/${contactId}`);
      
      // ✅ CACHE INVALIDATION: Invalidate contacts after deletion
      invalidateContactQueries();
      console.log('🔄 Cache invalidated after contact deletion');
      
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting contact:', error);
      throw error;
    }
  },

  // Search contacts by email for document sharing
  async searchContactsByEmail(email) {
    try {
      const response = await api.get(`/contacts?search=${encodeURIComponent(email)}&limit=10`);
      return response.data.contacts || [];
    } catch (error) {
      console.error('Error searching contacts:', error);
      throw error;
    }
  }
}; 